import React from 'react';
import { makeStyles } from '@material-ui/core';
import { Link as ReactRouterLink } from 'react-router';

import { NormalizedAlgoliaListing } from 'ha/models/Listing/types';
import { useIntl } from 'ha/i18n';
import { buildParams } from '@ha/search-query';
import { Card, Link } from '@ha/dsl';

import PhotoGalleryWithControls from './PhotoGalleryWithControls';
import Info from './Info';

const useStyles = makeStyles(theme => ({
  card: {
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
    },
    height: '100%',
    width: '100%',
  },
  cardLink: {
    textDecoration: 'none',
  },
}));

interface Props {
  room: NormalizedAlgoliaListing;
  isLazy?: boolean;
  scrollableElement?: HTMLElement | 'window';
  bottomOffset?: string;
  withGallery?: boolean;
  withFavorites?: boolean;
  noFollow?: boolean;
  onMouseEnter?: (id: number) => void;
  onMouseLeave?: (id: number) => void;
  onCardClick?: () => void;
  queryParams?: {
    startDate: string | null;
    endDate: string | null;
  };
  linkTarget?: '_blank';
  // used in analytics
  cardLocation?: string;
}

export const ListingCard: React.FC<Props> = ({
  onMouseEnter,
  onMouseLeave,
  room,
  withGallery,
  withFavorites,
  isLazy,
  scrollableElement,
  bottomOffset = '-50%',
  onCardClick,
  queryParams = {},
  noFollow,
  linkTarget,
  cardLocation = 'unspecified',
}) => {
  const { urlResolver } = useIntl();
  const classes = useStyles();

  const handleMouseEnter = React.useCallback(() => {
    if (onMouseEnter) {
      onMouseEnter(room.id);
    }
  }, [onMouseEnter, room.id]);

  const handleMouseLeave = React.useCallback(() => {
    if (onMouseLeave) {
      onMouseLeave(room.id);
    }
  }, [onMouseLeave, room.id]);

  const LinkComponent = linkTarget === '_blank' ? Link : ReactRouterLink;

  const redirectLink = React.useMemo(
    () =>
      `${urlResolver.formatHrefURL(room.listingPath)}${buildParams(
        queryParams,
      )}`,
    [room.listingPath, queryParams, urlResolver],
  );

  return (
    <LinkComponent
      to={redirectLink}
      target={linkTarget}
      rel={noFollow ? 'noopener noreferrer' : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-test-locator="Listing Card"
      onClick={onCardClick}
      itemScope
      itemType="http://schema.org/Offer"
      className={classes.cardLink}
    >
      <Card elevation={1} className={classes.card}>
        <PhotoGalleryWithControls
          room={room}
          isLazy={isLazy}
          scrollableElement={scrollableElement}
          bottomOffset={bottomOffset}
          cardLocation={cardLocation}
          withGallery={withGallery}
          withFavorites={withFavorites}
        />
        <Info room={room} />
      </Card>
    </LinkComponent>
  );
};

export default ListingCard;
